'use client';

import * as React from 'react';

import { cn } from '@banzai/ui/lib/utils';
import { RiLogoutCircleLine } from 'react-icons/ri';
import { ReloadIcon } from '@radix-ui/react-icons';
import { FaGoogle } from 'react-icons/fa';
import { Button } from '@banzai/ui/components/button';
import { Input } from '@banzai/ui/components/input';
import { Label } from '@banzai/ui/components/label';
import { useCookies } from 'next-client-cookies';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import * as gravatar from 'gravatar';
import { Avatar, AvatarFallback, AvatarImage } from '@banzai/ui/components/avatar';
import { useTheme } from '@banzai/themes/use-theme';
import Link from 'next/link';

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {}

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string | null>('');
  const [password, setPassword] = React.useState<string | null>('');
  const [isEmailValid, setIsEmailValid] = React.useState<boolean>(false);
  const [isOnEmailPage, setisOnEmailPage] = React.useState<boolean>(true);
  const [isPasswordValid, setisPasswordValid] = React.useState<boolean>(false);
  const [hasError, setHasError] = React.useState<boolean>(false);
  const [doShake, setDoShake] = React.useState<boolean>(false);
  const [gravatarUrl, setGravatarUrl] = React.useState<string | null>(null);
  const [avatarFallback, setAvatarFallback] = React.useState<string | null>(null);
  const themeConfig = useTheme();

  const shakeAnimation = 'animate-shake animate-twice animate-duration-200';

  const passwordRef = React.useRef<any>(null);

  const router = useRouter();
  const cookies = useCookies();

  async function moveToPassword(event: React.SyntheticEvent) {
    event.preventDefault();
    setIsLoading(true);

    setTimeout(() => {
      setisOnEmailPage(false);
      setTimeout(() => {
        setIsLoading(false);
        if (passwordRef.current) {
          passwordRef.current.disabled = false;
          passwordRef.current.focus();
        }
      }, 150);
    }, 500);
  }

  function validateEmail(newEmail: string) {
    const newIsValid =
      !!newEmail && !!newEmail.length && newEmail.indexOf('@') > 0 && newEmail.indexOf('.') > 0;
    if (newIsValid != isEmailValid) {
      setIsEmailValid(newIsValid);
    }
    if (newEmail != email) {
      setEmail(newEmail);
    }
    const newGravatarUrl = gravatar.url(newEmail, {
      default: 'https://www.hostpapa.com/knowledgebase/app/uploads/2018/04/1-13.png',
    });
    if (newGravatarUrl != gravatarUrl) {
      setGravatarUrl(newGravatarUrl);
    }

    const newAvatarFallback =
      newEmail && newEmail.length && newEmail.trim().length ? newEmail.trim()[0] : 'B';

    if (newAvatarFallback != avatarFallback) {
      setAvatarFallback(newAvatarFallback as string);
    }
  }

  function validatePassword(newPassword: string) {
    const newIsValid = !!newPassword && !!newPassword.length;
    if (newIsValid != isPasswordValid) {
      setisPasswordValid(newIsValid);
    }
    if (newPassword != password) {
      setPassword(newPassword);
    }
  }

  if (email) {
    validateEmail(email);
  }

  if (password) {
    validatePassword(password);
  }

  const submit = async (e: any) => {
    e.preventDefault();

    setIsLoading(true);

    const callbackUrl = cookies.get('banzai.callback-url');
    const result = (await signIn('credentials', {
      redirect: false,
      email: email,
      password: password,
      callbackUrl,
    })) as any;

    setTimeout(() => {
      if (result.error) {
        setDoShake(true);
        setHasError(true);
        setTimeout(() => {
          setDoShake(false);
          validatePassword('');
          setIsLoading(false);
          if (passwordRef.current) {
            passwordRef.current.disabled = false;
            passwordRef.current.focus();
          }
        }, 400);
        return;
      }
      let newUrl = result.url || '/';
      if (!URL.canParse(newUrl)) {
        newUrl = '/';
      }
      const checkUrl = new URL(newUrl);
      if (
        checkUrl.host === window.location.host &&
        checkUrl.port === window.location.port &&
        checkUrl.pathname === window.location.pathname
      ) {
        newUrl = '/';
      }
      router.push(newUrl);
    }, 500);
  };

  function resetForm(e: any) {
    e.preventDefault();

    setTimeout(() => {
      setIsLoading(true);
      setisOnEmailPage(true);
      validateEmail('');

      setTimeout(() => {
        setIsLoading(false);
        validatePassword('');
        setHasError(false);
      }, 200);
    }, 200);
  }

  const invalidRing = 'focus-visible:ring-destructive ring-destructive ring-1';

  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight font-logo">
          {themeConfig.theme.login.title}
        </h1>
        <p className="text-sm text-muted-foreground">{themeConfig.theme.login.subTitle}</p>
      </div>

      <div className={cn('grid gap-6 overflow-hidden', className)} {...props}>
        <div className="grid relative">
          <div>
            <form
              onSubmit={moveToPassword}
              className={`grid gap-2 absolute container resize-none p-1 transition-transform ${isOnEmailPage ? 'translate-x-0' : '-translate-x-full'}`}>
              <div>
                <Label className="sr-only" htmlFor="email">
                  Email
                </Label>
                <Input
                  id="email"
                  placeholder="name@example.com"
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  value={email as any}
                  disabled={isLoading || !isOnEmailPage}
                  onChange={e => {
                    validateEmail(e.target.value);
                  }}
                />
              </div>

              <Button disabled={isLoading || !isEmailValid || !isOnEmailPage}>
                {isLoading && <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />}
                Sign In with Email
              </Button>
              <div>
                <div className="text-sm text-muted-foreground text-center">
                  Forgot your password? Click{' '}
                  <Link
                    href="/login/password-reset"
                    className="underline underline-offset-4 hover:text-primary">
                    here
                  </Link>{' '}
                  to reset it.
                </div>
              </div>
            </form>
          </div>
          <div>
            <form
              onSubmit={submit}
              className={`grid gap-2 ${doShake ? shakeAnimation : ''} ${isOnEmailPage ? 'translate-x-full' : 'translate-x-0'} p-1 transition-transform`}>
              <div className="rounded-full bg-secondary ml-5 mr-5 mb-2 flex items-center">
                <Avatar className="h-11 w-11">
                  <AvatarImage src={gravatarUrl as any} alt="@shadcn" />
                  <AvatarFallback>{avatarFallback}</AvatarFallback>
                </Avatar>
                <div className="text-sm m-3 flex-grow">
                  {email && email.length > 20 ? email.substring(0, 20) + '...' : email}
                </div>
                <Button
                  disabled={isLoading}
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="rounded-full ml-auto h-11 w-14">
                  <RiLogoutCircleLine className="w-6 h-6" />
                </Button>
              </div>

              <div>
                <Label className="sr-only" htmlFor="password">
                  Password
                </Label>
                <Input
                  ref={passwordRef}
                  className={hasError ? invalidRing : ''}
                  id="password"
                  placeholder="Password"
                  type="password"
                  autoCapitalize="none"
                  value={password as any}
                  autoCorrect="off"
                  disabled={isLoading || isOnEmailPage}
                  onChange={e => {
                    validatePassword(e.target.value);
                  }}
                />
              </div>
              <Button disabled={isLoading || !isEmailValid || isOnEmailPage}>
                {isLoading && <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />}
                Login
              </Button>
              <div>
                <div className="text-sm text-muted-foreground text-center">
                  Forgot your password? Click{' '}
                  <Link
                    href="/login/password-reset"
                    className="underline underline-offset-4 hover:text-primary">
                    here
                  </Link>{' '}
                  to reset it.
                </div>
              </div>
            </form>
          </div>
        </div>

        {false && (
          <div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>
            <Button variant="outline" type="button" disabled={isLoading}>
              {isLoading ? (
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FaGoogle className="mr-2 h-4 w-4" />
              )}{' '}
              Google
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
